import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import DownItems from '../../../../assets/Images/down-arrow.svg';
import DragItems from '../../../../assets/Images/drag.svg';
import UpItems from '../../../../assets/Images/up-arrow.svg';
import { MoveProcessEnum } from '../../../../constants/enums';
import { IAddEditFormProps } from '../../../../models/forms/IForm';
import FormLoader from '../../../../shared-components/FormLoader/FormLoader';
import ModalFooter from '../../../../shared-components/modal/modal-footer';
import { formatMessage } from '../../../../translations/format-message';

export interface IHKRColumnItem {
  id: string;
  name: string;
  IsHidden: boolean;
}

interface IProps extends IAddEditFormProps<any> {
  // every HKR column available
  allColumns: string[];
  // currently visible columns, in their saved order
  selectedColumns: string[];
}

const HKRColumnReorderForm = (props: IProps) => {
  const [items, setItems] = useState<IHKRColumnItem[]>([]);

  useEffect(() => {
    // Visible columns first (in saved order), hidden ones after.
    const selected = props.selectedColumns.filter((c) =>
      props.allColumns.includes(c)
    );
    const hidden = props.allColumns.filter((c) => !selected.includes(c));
    setItems(
      [...selected, ...hidden].map((name) => ({
        id: name,
        name,
        IsHidden: !selected.includes(name),
      }))
    );
  }, [props.allColumns, props.selectedColumns]);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const reordered = Array.from(items);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);

    setItems(reordered);
  };

  const moveItem = (index: number, direction: MoveProcessEnum) => {
    const newIndex = direction === MoveProcessEnum.Up ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;

    const updated = [...items];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];

    setItems(updated);
  };

  const handleHideToggle = (id: string) => {
    setItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, IsHidden: !i.IsHidden } : i))
    );
  };

  return (
    <Formik
      initialValues={props.initialValues}
      enableReinitialize
      validateOnBlur={false}
      onSubmit={async () => {
        props.submitHandler(items);
      }}
    >
      {() => (
        <Form className="user-form">
          {props.isLoading ? (
            <FormLoader loading={props.isLoading} contained />
          ) : null}
          <Modal.Body>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="hkr-columns">
                {(provided) => (
                  <div className="order-column">
                    <table
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="min-w-full table-auto border border-gray-300 w-100"
                    >
                      <thead>
                        <tr className="bg-gray-200">
                          <th className="border px-4 py-2 text-center w-16">
                            <span className="ms-1">
                              {formatMessage('hide_column')}
                            </span>
                          </th>
                          <th className="border px-4 py-2">
                            {formatMessage('name')}
                          </th>
                          <th className="border px-4 py-2">
                            {formatMessage('actions')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, index) => (
                          <Draggable
                            key={item.id}
                            draggableId={item.id}
                            index={index}
                          >
                            {(provided) => (
                              <tr
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className="border-b"
                              >
                                <td
                                  className="border px-4 py-2 text-center"
                                  onClick={(e) => e.stopPropagation()} // keep checkbox from starting a drag
                                >
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={item.IsHidden}
                                    onChange={() => handleHideToggle(item.id)}
                                    aria-label={`Hide ${item.name}`}
                                  />
                                </td>
                                <td className="border px-4 py-2">
                                  <img
                                    src={DragItems}
                                    alt=""
                                    className="drag me-2"
                                  />{' '}
                                  {item.name}
                                </td>
                                <td className="border px-4 py-2 space-x-2 bg-red-100 up-down-items last-column">
                                  <div className="d-flex">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        moveItem(index, MoveProcessEnum.Up)
                                      }
                                      disabled={index === 0}
                                      className={`px-2 py-1 bg-green-600 rounded btn-light-primary btn-sm ${index === 0 && 'disabled-li'}`}
                                    >
                                      <img
                                        src={UpItems}
                                        alt=""
                                        className="drag"
                                      />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        moveItem(index, MoveProcessEnum.Down)
                                      }
                                      disabled={index === items.length - 1}
                                      className={`px-2 py-1 bg-green-600 rounded btn-light-danger btn-sm ${index === items.length - 1 && 'disabled-li'}`}
                                    >
                                      <img
                                        src={DownItems}
                                        alt=""
                                        className="drag"
                                      />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </tbody>
                    </table>
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Modal.Body>
          <ModalFooter
            id={props.id}
            onModalClosed={props.onModalClosed}
            isLoading={props.isLoading}
            saveNextBtn={'save'}
            backCancelBtn="cancel"
          />
        </Form>
      )}
    </Formik>
  );
};

export default HKRColumnReorderForm;
